import { Img } from '@/components/builder2/utils/Img';
import React from 'react';
import { useNode } from '@craftjs/core';
import { isBlankHtml } from '@/utils/functions';
import { trackConversionFromHref } from '@/lib/track-site-event';
import { htmlSeguro } from '../utils/htmlSeguro';

interface CardProps {
  image?: string;
  imageAlt?: string;
  imageHeight?: number;
  title?: string;
  text?: string;
  buttonText?: string;
  buttonLink?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  titleColor?: string;
  textColor?: string;
  bgColor?: string;
  borderColor?: string;
  borderRadius?: number;
  shadow?: boolean;
  padding?: number;
  align?: 'left' | 'center';
}

/**
 * Tarjeta — render del sitio público.
 *
 * Imagen opcional arriba, título, texto y botón opcional. Pensada para soltar
 * varias dentro de una `Grid` y armar servicios, beneficios o destacados sin
 * depender de una sección entera.
 *
 * Espejo de goautos-admin/src/components/builder2/userComponents/Card.tsx.
 */
export const Card = ({
  image = '',
  imageAlt = '',
  imageHeight = 180,
  title = '',
  text = '',
  buttonText = '',
  buttonLink = '#',
  buttonBgColor = '#1e88e5',
  buttonTextColor = '#ffffff',
  titleColor = '#111827',
  textColor = '#4b5563',
  bgColor = '#ffffff',
  borderColor = '#e5e7eb',
  borderRadius = 12,
  shadow = true,
  padding = 20,
  align = 'left',
}: CardProps) => {
  const { connectors } = useNode();

  const alto = Math.max(0, Number(imageHeight) || 0);

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      className={`flex h-full w-full flex-col overflow-hidden ${shadow ? 'shadow-md' : ''}`}
      style={{
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: `${borderRadius}px`,
        textAlign: align,
        // Igual que en `Section`: el editor lo necesita para anclar sus controles
        // y acá no cambia nada, pero mantiene idéntico el estilo computado.
        position: 'relative',
      }}
    >
      {!!image && (
        <Img loading="eager" sizes="(max-width: 768px) 100vw, 50vw"
          src={image}
          alt={imageAlt}
          className="block w-full"
          style={{ height: alto > 0 ? `${alto}px` : 'auto', objectFit: 'cover' }}
        />
      )}
      <div className="flex flex-1 flex-col" style={{ padding: `${padding}px` }}>
        {!isBlankHtml(title) && (
          <h3
            className="mb-2 text-lg font-semibold"
            style={{ color: titleColor }}
            dangerouslySetInnerHTML={htmlSeguro(title || '')}
          />
        )}
        {!isBlankHtml(text) && (
          <div
            className="text-sm leading-relaxed"
            style={{ color: textColor }}
            dangerouslySetInnerHTML={htmlSeguro(text || '')}
          />
        )}
        {!isBlankHtml(buttonText) && (
          <a
            href={buttonLink || '#'}
            onClick={() => trackConversionFromHref(buttonLink || '')}
            className="mt-4 inline-block self-start rounded-lg px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
            style={{
              background: buttonBgColor,
              color: buttonTextColor,
              alignSelf: align === 'center' ? 'center' : 'flex-start',
            }}
          >
            <span dangerouslySetInnerHTML={htmlSeguro(buttonText || '')} />
          </a>
        )}
      </div>
    </div>
  );
};

Card.craft = {
  displayName: 'Card',
  props: {
    image: '',
    imageAlt: '',
    imageHeight: 180,
    title: 'Título de la tarjeta',
    text: 'Una descripción breve de lo que ofrece esta tarjeta.',
    buttonText: '',
    buttonLink: '#',
    buttonBgColor: '#1e88e5',
    buttonTextColor: '#ffffff',
    titleColor: '#111827',
    textColor: '#4b5563',
    bgColor: '#ffffff',
    borderColor: '#e5e7eb',
    borderRadius: 12,
    shadow: true,
    padding: 20,
    align: 'left',
  },
};
